import type { LucideIcon } from 'lucide-react';
import { ChevronDown } from 'lucide-react';
import type { ReactNode } from 'react';
import { useSidebar } from './SidebarContext';

interface SidebarSectionProps {
  // Stable id used to persist collapsed/expanded state in localStorage.
  id: string;
  label: string;
  icon?: LucideIcon;
  // Optional count shown as a pill in the header (e.g. active lead count).
  badge?: number;
  // Open/closed state when no value is persisted yet. Defaults to expanded.
  defaultOpen?: boolean;
  children: ReactNode;
}

/**
 * A collapsible navigation group with a clickable header and smooth
 * expand/collapse animation. Expanded state persists via SidebarContext.
 */
export function SidebarSection({
  id,
  label,
  icon: Icon,
  badge,
  defaultOpen = true,
  children,
}: SidebarSectionProps) {
  const { isSectionOpen, toggleSection } = useSidebar();
  const open = isSectionOpen(id, defaultOpen);

  return (
    <div>
      <button
        type="button"
        onClick={() => toggleSection(id, defaultOpen)}
        aria-expanded={open}
        className="flex min-h-10 w-full items-center gap-3 rounded-md px-3 py-2 text-sm font-bold text-sidebar-foreground transition-colors hover:bg-sidebar-accent"
      >
        {Icon && <Icon size={18} className="text-muted-foreground" />}
        {/* The chevron sits right after the label, not at the row's end, so
            the badge stays in the same right-hand column as the plain links. */}
        <span className="flex min-w-0 flex-1 items-center gap-1">
          <span className="truncate text-left">{label}</span>
          <ChevronDown
            size={16}
            className={`shrink-0 text-muted-foreground transition-transform duration-200 ${
              open ? 'rotate-0' : '-rotate-90'
            }`}
          />
        </span>
        {badge !== undefined && badge > 0 && (
          <span className="shrink-0 rounded-full bg-sidebar-accent px-1.5 py-0.5 text-xs font-medium text-muted-foreground">
            {badge}
          </span>
        )}
      </button>

      {/* Grid-rows trick gives a smooth height transition without measuring. */}
      <div
        className={`grid transition-all duration-200 ease-in-out ${
          open ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'
        }`}
      >
        <div className="overflow-hidden">
          <div className="mt-1 space-y-0.5">{children}</div>
        </div>
      </div>
    </div>
  );
}
